import z from "zod";

export interface UpdateIntegrationStatusInput {
  status: boolean;
  source: string;
}

/** One row of `GET /v1/list/integration/sources`. */
export interface IntegrationSource {
  /** Vendor key — "ninjaOne" | "level" | "datto" | "ncentral" | "xero" | … */
  name: string;
  description: string | null;
  is_connected: boolean;
  is_active: boolean;
  synced_at: string | null;
  /**
   * The vendor host the MSP connected with — bare host or full origin, backend is
   * not consistent (`app.ninjarmm.com`, `https://integration3.n-able.com`). Datto
   * returns the API host (`…-api.centrastage.net`), not the web console. `null` for
   * non-RMM sources and anything not connected.
   */
  url: string | null;
  created_by?: string | null;
}

export interface GetRemoteDeviceListInput {
  customerUserId: string;
  sourceName: string;
}

export interface RemoteDevice {
  device_id?: string | number;
  id?: string | number;
  mapping_id?: string;
  device_name?: string;
  name?: string;
  source?: string;
  base_url?: string;
  remote_control_uri?: string;
  external_customer_id?: string;
  meta_data?: {
    portalUrl?: string;
    webRemoteUrl?: string;
    [k: string]: unknown;
  };
}

export const NCentralCustomersSchema = z.object({
  customer_id: z.string(),
  customer_name: z.string(),
  ncentral_customer_id: z.number(),
});

export const NCentralDeviceSchema = z.array(
  z.object({
    deviceId: z.number(),
    uri: z.string().nullable().optional(),
    remoteControlUri: z.string().nullable().optional(),
    sourceUri: z.string().nullable().optional(),
    longName: z.string(),
    deviceClass: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    isProbe: z.boolean().nullable().optional(),
    osId: z.string().nullable().optional(),
    supportedOs: z.string().nullable().optional(),
    discoveredName: z.string().nullable().optional(),
    deviceClassLabel: z.string().nullable().optional(),
    supportedOsLabel: z.string().nullable().optional(),
    lastLoggedInUser: z.string().nullable().optional(),
    stillLoggedIn: z.string().nullable().optional(),
    licenseMode: z.string().nullable().optional(),
    customerId: z.number(),
    soName: z.string().nullable().optional(),
    customerName: z.string().nullable().optional(),
    siteName: z.string().nullable().optional(),
  }),
);

export interface AddNCentralConfigInput {
  webToken: string;
  baseUrl: string;
  board: string;
}

export interface IntegrationCustomerMappingInput {
  customerData: Array<{
    external_customer_id: string;
    external_customer_name: string;
    deskday_customer_id: string;
    deskday_customer_name: string;
  }>;
  source: string;
}

export interface NcentralDeviceMappingInput {
  deviceMapping: Array<{
    mapping_id: string;
    devices: Array<{
      device_id: string;
      device_name: string;
      deskday_customer_user_id: string;
      deskday_customer_user_name: string;
      remote_control_uri: string;
      meta_data: Record<string, unknown>;
    }>;
  }>;
  source: string;
}

export interface UpdateNcentralRemoteCredentialsInput {
  username: string;
  password: string;
}
export const NCentralConfigSchema = z
  .object({
    id: z.string(),
    msp_id: z.string(),
    web_token: z.string(),
    base_url: z.string(),
    board: z.string(),
    created_by: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    updated_by: z.string(),
    is_deleted: z.boolean(),
  })
  .nullable();

export interface NinjaCustomerMappingInput {
  customerData: Array<{
    external_customer_id: string;
    external_customer_name: string;
    deskday_customer_id: string;
    deskday_customer_name: string;
  }>;
  source: string;
}

export interface NinjaOneDeviceMappingInput {
  deviceMapping: Array<{
    mapping_id: string;
    external_customer_id: number;
    deskday_customer_id: string;
    devices: Array<{
      device_id: string;
      device_name: string;
      deskday_customer_user_id: string;
      deskday_customer_user_name: string;
      remote_control_uri: string;
      meta_data: Record<string, unknown>;
    }>;
    unmapped: Array<{
      device_id: string;
      deskday_customer_user_id: string;
    }>;
  }>;
  source: string;
}

export interface MSPConfiguration {
  board: string;
}

interface TaxRate {
  deskday_tax_id: string;
  deskday_tax_name: string;
  external_tax_id: string;
  external_tax_name: string;
}

export interface IntegrationTaxMappingInput {
  taxData: TaxRate[];
  source: string;
}

interface ProductData {
  deskday_product_id: string;
  deskday_product_name: string;
  external_product_id: string;
  external_product_name: string;
}

export interface IntegrationProductMappingInput {
  products: ProductData[];
  source: string;
}

export interface UpdateLevelAuthConfigInput {
  api_key: string;
  webhook_url: string;
  webhook_secret: string;
  board: string;
}

export interface UpsertHuduAuthConfigInput {
  msp_name: string;
  hudu_url: string;
  hudu_api_key: string;
}

export type MapLevelCustomersInput = Array<{
  deskday_customer_id: string;
  deskday_customer_name: string;
  external_customer_id: string;
  external_customer_name: string;
}>;

/** Customer as returned by the Level RMM customers listing. */
export interface LevelCustomer {
  id: string;
  name: string;
}

/** An already-saved Level ↔ DeskDay customer mapping. */
export interface LevelMappedCustomer {
  mapping_id: string;
  external_customer_id: string;
  external_customer_name: string;
  deskday_customer_id: string;
  deskday_customer_name: string;
}

/** Device as returned by the Level RMM devices listing. */
export interface LevelDevice extends Record<string, unknown> {
  id: string;
  hostname: string;
  /** Level's customer grouping; null for ungrouped devices. */
  group_id: string | null;
}

/** An already-saved Level device ↔ DeskDay user mapping. */
export interface LevelMappedDevice {
  device_id: string;
  deskday_customer_user_id: string;
  deskday_customer_user_name: string;
}

/** A row of the device-mapping table, one per Level device. */
export interface LevelDeviceMappingRow {
  device_id: string;
  device_name: string;
  deskday_customer_user_id: string;
  deskday_customer_user_name: string;
  meta_data: Record<string, unknown>;
}

/** Device-mapping rows grouped under the customer they belong to. */
export interface LevelDeviceMappingGroup {
  mapping_id: string;
  external_customer_id: string;
  deskday_customer_id: string;
  devices: LevelDeviceMappingRow[];
}

/**
 * A row of the customer-mapping table. The DeskDay side is nullable because a
 * Level customer starts unmapped and can be cleared again.
 */
export interface LevelCustomerMappingRow {
  external_customer_id: string;
  external_customer_name: string;
  deskday_customer_id: string | null;
  deskday_customer_name: string | null;
}

export interface MapLevelDevicesInput {
  device_mapping: Array<{
    external_customer_id: string;
    deskday_customer_id: string;
    devices: Array<{
      device_id: string;
      device_name: string;
      deskday_customer_user_id: string;
      deskday_customer_user_name: string;
      meta_data: Record<string, unknown>;
    }>;
  }>;
}

export interface CreateDattoAuthConfigInput {
  api_key: string;
  api_secret_key: string;
  api_url: string;
}

export interface UpdateDattoAuthConfigInput {
  api_key: string;
  api_secret_key: string;
  api_url: string;
  board: string;
}

export type MappDattoCustomersInput = MapLevelCustomersInput;
export type MapDattoDevicesInput = MapLevelDevicesInput;

export interface UpsertIntegrationNotificationSettingsInput {
  source_id: string;
  is_enabled: boolean;
}

export interface StripeConnectStatus {
  is_connected: boolean;
  is_active?: boolean;
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
  state?: string;
  stripe_account_id?: string;
  default_currency?: string;
  country?: string;
  stripe_publishable_key?: string;
}

export interface ConnectPax8Input {
  client_id: string;
  client_secret: string;
}

export type Pax8ApplyTo = "all" | "selected";

export type Pax8CustomerSyncScope = "existing" | "all";

export interface Pax8AutoMapSettings {
  auto_map_customers: boolean;
  customer_auto_sync_scope?: Pax8CustomerSyncScope;
  auto_map_subscriptions: boolean;
  subscription_apply_to: Pax8ApplyTo;
  subscription_customer_ids: string[];
  subscription_auto_sync_scope?: Pax8CustomerSyncScope;
  notify_failed: boolean;
  notify_apply_to: Pax8ApplyTo;
  notify_role_ids: string[];
  /**
   * Read-only, and not a setting: when the MSP last completed the
   * customer-mapping import. null means never, i.e. they have not been through
   * the connect wizard, so Configure should open the auto-map settings screen
   * rather than jumping to customer mapping.
   */
  customers_last_imported_at?: string | null;
  /** Read-only: when a sync last completed. */
  last_synced_at?: string | null;
  /**
   * Read-only, and not a setting: non-null while a sync is running. Lets the
   * card show progress and refuse a second click rather than discovering the
   * backend's conflict response.
   */
  sync_started_at?: string | null;
}

export type Pax8MappingStatus =
  | "auto_mapped"
  | "needs_review"
  | "auto_created"
  | "un_mapped";

export interface Pax8CustomerMappingRow {
  mapping_id: string;
  external_customer_id: string;
  external_customer_name: string;
  proposed_deskday_customer_id: string | null;
  proposed_deskday_customer_name: string | null;
  resolved_deskday_customer_id: string | null;
  resolved_deskday_customer_name: string | null;
  status: Pax8MappingStatus;
  is_new: boolean;
  apply_error_reason?: string | null;
}

export interface Pax8CustomerMappingCounts {
  all: number;
  needs_review: number;
  auto_mapped: number;
  auto_created: number;
  un_mapped: number;
}

export interface Pax8CustomerMappingList {
  rows: Pax8CustomerMappingRow[];
  counts: Pax8CustomerMappingCounts;
  customers_last_imported_at: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface Pax8SyncSummary {
  processed: number;
  mapped: number;
  created: number;
  needs_review: number;
}

/** A single customer that failed to import (create) during the apply step. */
export interface Pax8ImportFailure {
  external_customer_name: string;
  reason: string;
}

/**
 * Result of the last customer-mapping Import — returned by the apply call and
 * persisted so the mapping page's error state ("Log" drawer of failed uploads)
 * survives reload. `failures` is capped server-side; `failed` is the true count.
 */
export interface Pax8ApplyResult {
  imported_at: string;
  attempted: number;
  passed: number;
  failed: number;
  failures: Pax8ImportFailure[];
}

// Subscription mapping has only two states: a Pax8 subscription is either
// auto_mapped   linked to a DeskDay recurring invoice
// needs_review  cannot be linked yet — either no DeskDay customer is mapped, or
//               that customer has no eligible recurring invoice
// un_mapped     an operator deliberately un-linked it; a sync never re-attaches
//
// Unlike customer mapping there is no auto_created: DeskDay has no internal
// subscription-creation module, so a subscription is never conjured — it is
// linked, blocked, or deliberately excluded.
export type Pax8SubscriptionStatus =
  | "auto_mapped"
  | "needs_review"
  | "un_mapped";

export interface Pax8SubscriptionMappingRow {
  mapping_id: string;
  external_subscription_id: string;
  product_name: string;
  sku: string | null;
  vendor: string | null;
  vendor_logo_url: string | null;
  invoice_alias: string | null;
  customer_id: string | null;
  customer_name: string | null;
  quantity: number;
  vendor_cost: number;
  sell_price: number;
  currency: string;
  recurring_invoice_id: string | null;
  recurring_invoice_name: string | null;
  status: Pax8SubscriptionStatus;
  apply_error_reason?: string | null;
}

export interface Pax8SubscriptionCounts {
  all: number;
  auto_mapped: number;
  needs_review: number;
  un_mapped: number;
}

export interface Pax8SubscriptionMappingsResponse {
  rows: Pax8SubscriptionMappingRow[];
  counts: Pax8SubscriptionCounts;
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  subscriptions_last_imported_at: string | null;
}

/**
 * Body for the subscription bulk-action endpoint. Two actions:
 *   - "un_map": stop billing the selected rows and keep them stopped.
 *   - "set_invoice": assign ONE recurring invoice to all selected rows,
 *     mirroring the single-row re-point (updatePax8SubscriptionMapping).
 * Backend endpoint: PATCH `${VITE_APP_INTEGRATION_BASE_URL}/v1/pax8/subscription-mappings/bulk`
 */
export type Pax8BulkUpdateSubscriptionMappingsBody =
  | { mapping_ids: string[]; action: "un_map" }
  | {
      mapping_ids: string[];
      action: "set_invoice";
      recurring_invoice_id: string;
      recurring_invoice_name?: string;
    };

/** Result of a subscription bulk action — shape differs by `action`. */
export interface Pax8BulkUnmapSubscriptionsResult {
  unmapped: number;
  failed: number;
  not_found: number;
  failures: Array<{ mapping_id: string; message: string }>;
}

export interface Pax8BulkSetInvoiceSubscriptionsResult {
  updated: number;
  failed: number;
  failures: Array<{ mapping_id: string; message: string }>;
}

export interface Pax8SubscriptionApplyResult {
  applied: number;
  failed: number;
  /**
   * The durable log's own shape, returned alongside the counts so the Log
   * drawer's cache can be PRIMED from this response rather than refetched —
   * an invalidate+refetch flashes an empty drawer on the first failed import.
   * Optional because a backend that predates it simply omits it, and the
   * caller falls back to invalidating.
   */
  import_result?: Pax8SubscriptionApplyLog;
}

/** A single subscription that failed to apply (bill) during the apply step. */
export interface Pax8SubscriptionImportFailure {
  recurring_invoice_name: string;
  reason: string;
}

/**
 * Result of the last subscription-mapping Import — persisted so the mapping
 * page's error state ("Log" drawer of failed uploads) survives reload, same
 * as `Pax8ApplyResult` for the customer step. `failures` is capped
 * server-side; `failed` is the true count.
 */
export interface Pax8SubscriptionApplyLog {
  imported_at: string;
  attempted: number;
  passed: number;
  failed: number;
  failures: Pax8SubscriptionImportFailure[];
}

/**
 * The seven billing categories an invoice line can originate from. Fixed enum —
 * the backend rejects any other value with a 400.
 */
export type QbServiceCategory =
  | "tickets"
  | "projects"
  | "activities"
  | "contracts"
  | "adhoc_items"
  | "expenses"
  | "unmapped_products";

/** An item in the connected QuickBooks company, used to populate the picker. */
export interface QbItem {
  id: string;
  name: string;
  type: string;
  description?: string;
  unit_price?: number;
  sku?: string | null;
}

/** The paged envelope `GET /qb/products` returns under `data`. */
export interface QbItemList {
  totalCount: number;
  result: QbItem[];
  pagination: {
    page: number;
    perPage: number;
    startPosition: number;
    hasMore: boolean;
  };
}

/**
 * A saved category → QuickBooks item mapping. QuickBooks items carry no code,
 * so unlike the Xero equivalent there is no `*_item_code` field.
 */
export interface QbServiceCategoryMapping {
  mapping_id: string;
  service_category: QbServiceCategory;
  qb_item_id: string;
  qb_item_name: string;
}

/** One upsert entry for `POST /v1/qb/service-category-mapping`. */
export interface QbServiceCategoryMappingInput {
  service_category: QbServiceCategory;
  qb_item_id: string;
  qb_item_name: string;
}

/** A row of the service-category mapping table; the item side is empty when unmapped. */
export interface QbServiceCategoryMappingRow {
  service_category: QbServiceCategory;
  service_category_name: string;
  qb_item_id: string;
  qb_item_name: string;
}

/** The upserts and deletions a save must issue to reach the desired state. */
export interface QbServiceCategoryMappingDiff {
  upserts: QbServiceCategoryMappingInput[];
  deletions: QbServiceCategory[];
}

export type Pax8InvoiceInactiveReason = "not_started" | "ended" | "deactivated";

export interface Pax8RecurringInvoiceOption {
  mapping_id: string;
  invoice_name: string;
  is_active: boolean;
  invoice_start_date?: string | null;
  invoice_end_date: string | null;
  inactive_reason?: Pax8InvoiceInactiveReason | null;
}
